import {
    ChangeDetectorRef,
    ChangeDetectionStrategy,
    OnInit,
    Component,
    Input,
    signal,
    ViewEncapsulation,
    ElementRef,
    Renderer2,
    HostListener,
    ViewChild,
    OnDestroy,
    computed,
} from "@angular/core";
import { S25States } from "../../services/state/state.map";
import { Proto } from "../../pojo/Proto";
import { TaskService } from "../../services/task/task.service";
import { S25Util } from "../../util/s25-util";
import { StateService } from "../../services/state.service";
import { ContactService } from "../../services/contact.service";
import { NotificationService, PostBarNotification } from "../../services/notification.service";
import { AuthService } from "../../services/auth.service";
import { S25Const } from "../../util/s25-const";
import { LoginService } from "../../services/login.service";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { PreferenceService } from "../../services/preference.service";
import { InstCssService } from "../../services/intscss.service";
import { FlsService } from "../../services/fls.service";
import { MoreMenuUtil } from "./more-menu/more.menu.util";
import { ModalService } from "../modal/modal.service";
import { Debounce } from "../../decorators/debounce.decorator";
import { UserprefService } from "../../services/userpref.service";
import { hasReadFls } from "../../pojo/Fls";
import { StandoutClassroomService } from "../../services/standout.classroom.service";
import { Subscription } from "rxjs";

@TypeManagerDecorator("s25-ng-nav-bar")
@Component({
    selector: "s25-ng-nav-bar",
    template: `
        <div class="row">
            <a class="skip-main" href="javascript:void(0);" (click)="scrollTo('mainContent')"
                >Skip to Content (Press Enter)</a
            >
            <div class="col-12" id="navbar-container" [class.logo-hidden]="!isVisible()">
                <div class="ngNav navbar">
                    <span class="c-navLogo">
                        <div class="layout">
                            <div class="header" role="banner">
                                <!-- this will be dynamical wrapped in a link if the preference has a value-->
                                <div class="logo" #logo role="img"></div>
                            </div>
                        </div>
                    </span>
                    <div class="" role="navigation" aria-label="primary">
                        <ul class="nav navbar-nav">
                            @if (!StateMap.dash.isHidden) {
                                <li class="c-mobileNav--hide">
                                    <a s25-ng-router-link [stateName]="StateNames.dash">
                                        <s25-ng-icon [type]="'schedulingAdvanced'"></s25-ng-icon>
                                        {{ StateMap.dash.display }}
                                    </a>
                                </li>
                            }
                            @if (!StateMap.rose.isHidden) {
                                <li class="c-nav-event">
                                    <a href="javascript:void(0)" (click)="goToRose()">
                                        <s25-ng-icon [type]="'navEdit'"></s25-ng-icon>
                                        <span>{{ StateMap.rose.display }}</span>
                                    </a>
                                </li>
                            }
                            @if (hasTaskPermissions) {
                                <li class="c-mobileNav--hide">
                                    <a
                                        s25-ng-router-link
                                        [stateName]="StateNames.search"
                                        [stateParams]="{
                                            viz: 'list',
                                            subject: 'task',
                                            search: taskDefault,
                                        }"
                                        [options]="{ source: 'state-service' }"
                                        class="task-notifications"
                                    >
                                        <s25-ng-icon [type]="'task'"></s25-ng-icon>
                                        <span class="text">Tasks</span>
                                        @if (tasksCount()) {
                                            <span class="count" [ngClass]="{ 'count--small': +tasksCount() < 9 }">
                                                {{ tasksCount() }}
                                            </span>
                                        }
                                    </a>
                                </li>
                            }
                            @if (this.hasChatPermissions) {
                                <li class="chat-link c-mobileNav--hide">
                                    <a [href]="socLink">
                                        Chat
                                        @if (hasUnreadChats()) {
                                            <span class="unread-dot"></span>
                                        }
                                    </a>
                                </li>
                            }
                            <li>
                                <span class="c-nav-username">{{ styledName() }}</span>
                            </li>
                            @if (isLoggedIn && masqueradeData) {
                                <li>
                                    <button (click)="logOut()" class="c-textButton" id="masqueradeLogout">
                                        Exit Masquerade
                                    </button>
                                </li>
                            }
                            @if (!isLoggedIn) {
                                <li class="sign-in-link">
                                    <a href="javascript:void(0)" class="c-nav-signin" (click)="login()">Sign In</a>
                                </li>
                            }
                        </ul>
                        <s25-ng-more-menu
                            [styledName]="styledName()"
                            (logIn)="login()"
                            (logOut)="logOut()"
                        ></s25-ng-more-menu>
                    </div>
                </div>
            </div>
        </div>
        <s25-ng-viewer-message></s25-ng-viewer-message>
    `,
    styles: `
        .task-notifications {
            display: flex;
            gap: 3px;
            align-items: center;
        }

        s25-ng-more-menu {
            min-height: 55px;
            display: flex;
            align-items: center;
        }

        .chat-link a {
            position: relative; /* anchor the dot to the link */
        }

        .unread-dot {
            position: absolute;
            width: 8px;
            height: 8px;
            background-color: red;
            border-radius: 50%;
        }
    `,
    standalone: false,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25NavBarComponent implements OnInit, OnDestroy {
    protected readonly StateMap = S25States.untyped;
    protected readonly StateNames = S25States.names;

    @Input() isLoggedIn: boolean;
    @Input() scrollTo: (id: string) => void; //function to be compatible with angjs version

    @ViewChild("logo", { static: false }) logoElement: ElementRef;

    logoLink: Proto.URLString = "";
    taskDefault: string; //Default task search
    adminMailLink: string;

    tasksCount = signal<string>("");
    hasTaskPermissions: boolean;
    hasChatPermissions: boolean;
    socLink = "";
    hasUnreadChats = signal(false);
    unreadChatsSubscription: Subscription;

    loginUrl: string;
    masqueradeData: PostBarNotification;

    // Scroll behavior signals - nav bar hides when scrolling up, shows when scrolling down in mobile app
    private scrollY = signal(0);
    private lastScrollY = signal(0);
    private scrollDirection = signal<"up" | "down">("up");
    private isScrolling = signal(false);

    // Computed signals for navbar behavior
    isAtTop = computed(() => this.scrollY() < 5);
    isVisible = computed(() => {
        // Always show when at top, or when scrolling up (but not actively scrolling down)
        return this.isAtTop() || (this.scrollDirection() === "up" && !this.isScrolling());
    });

    // Scroll handling properties
    private ticking = false;
    private scrollTimeout: NodeJS.Timeout;

    @HostListener("window:scroll")
    @HostListener("document:scroll")
    onWindowScroll() {
        if (!S25Const.inApp) return;
        this.handleScroll();
    }

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private renderer: Renderer2,
    ) {
        this.elementRef.nativeElement.angBridge = this;
    }

    async ngOnInit() {
        InstCssService.setInstCss(); //note: also sets up inst css colors, logo, etc

        if (S25Const.inApp) {
            // detect scroll for navbar show/hide in mobile app
            this.updateScrollState();
        }

        let secondaryP = this.getSecondaryData();
        await this.getBlockingData();
        await secondaryP; //wait for secondary data to be loaded
        this.getStyledName();
        this.updateTasksCount();
        this.cd.detectChanges();
    }

    private handleScroll() {
        if (!this.ticking) {
            requestAnimationFrame(() => this.updateScrollState());
            this.ticking = true;
        }
    }

    private updateScrollState() {
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        // Set scrolling state
        this.isScrolling.set(true);
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }
        this.scrollTimeout = setTimeout(() => {
            this.isScrolling.set(false);
        }, 150);

        // Update scroll position
        this.scrollY.set(currentScrollY);

        // Determine scroll direction with threshold to prevent jitter
        if (Math.abs(currentScrollY - this.lastScrollY()) > 3) {
            this.scrollDirection.set(currentScrollY > this.lastScrollY() ? "down" : "up");
            this.lastScrollY.set(currentScrollY);
        }

        this.ticking = false;
    }

    /**
     * data that can be loaded in the background
     */
    async getSecondaryData() {
        let prefsP = this.getPreferences();
        let flsP = FlsService.getFls();
        let groupP = UserprefService.getGroupId();
        await prefsP;
        let fls = await flsP;
        let groupId = await groupP;
        this.hasTaskPermissions = this.isLoggedIn && fls.TASK_LIST !== "N";
        this.hasChatPermissions = this.isLoggedIn && S25Const.socEnabled && (groupId === -1 || hasReadFls(fls?.CHAT));
        if (this.hasChatPermissions) {
            this.socLink = StandoutClassroomService.getChatLink();
            void this.checkUnreadChats();
        }
    }

    public async checkUnreadChats() {
        if (!this.hasChatPermissions) return;

        // Start the recurring check and get initial data
        try {
            const unreadChats = await StandoutClassroomService.getUnreadChatsWithRecurringCheck();
            this.hasUnreadChats.set(unreadChats.length > 0);
        } catch (error) {
            console.error("Error checking unread chats:", error);
            this.hasUnreadChats.set(false);
        }

        // Subscribe to updates from the service
        this.unreadChatsSubscription = StandoutClassroomService.getUnreadChatsObservable().subscribe((unreadChats) => {
            this.hasUnreadChats.set(unreadChats.length > 0);
            this.cd.detectChanges();
        });
    }

    @Debounce(300)
    @HostListener("window:updateTaskCount")
    async updateTasksCount() {
        if (!this.isLoggedIn) return;
        const [numTasks, error] = await S25Util.Maybe(TaskService.getHeaderTaskCount());
        if (numTasks > 0) this.tasksCount.set(numTasks.toString());
        else this.tasksCount.set("");

        this.cd.detectChanges();
    }

    styledName = signal<string>("Guest");
    //lazily fetch contact name (do NOT hold up app init to do this)
    async getStyledName() {
        const [styledName, error] = await S25Util.Maybe(ContactService.getCurrentNameStyled());
        if (styledName) {
            this.styledName.set(styledName);
            if (S25Util.getCookie(`S25Masquerade_${window.ProData.instanceId}`)) {
                this.masqueradeData = NotificationService.postBarNotification(
                    `Masquerading as ${this.styledName()}`,
                    false,
                    true,
                    false,
                    null,
                    "Exit",
                );
                this.displayMasqueradeBanner();
            }
            this.cd.detectChanges();
        }
    }

    displayMasqueradeBanner() {
        const navbar = this.elementRef.nativeElement.querySelector(".navbar") as HTMLElement;
        const bar = this.elementRef.nativeElement.querySelector(".notification-bar") as HTMLElement;

        if (navbar) {
            this.renderer.setStyle(navbar, "marginBottom", "unset");
        }

        if (bar) {
            this.renderer.setStyle(bar, "marginBottom", "1em");

            if (this.masqueradeData.isSticky) {
                this.renderer.setStyle(bar, "position", "sticky");
            }
        }
    }

    logOut(stateName?: string, keepRecentlyViewed?: boolean, keepJwt?: boolean) {
        if (this.masqueradeData) {
            S25Util.deleteCookie(`S25Masquerade_${window.ProData.instanceId}`);
            this.masqueradeData.remove();
        }

        return AuthService.invalidateAuth(stateName, keepRecentlyViewed, keepJwt).then(() => {
            S25Const.inApp && window.location.reload();
        });
    }

    goToRose() {
        StateService.gotoRose();
    }

    modalPromises: Promise<any>[] = [];

    async redirectToSSO() {
        const redirectUrl = `${this.loginUrl}${this.loginUrl.includes("?") ? "&" : "?"}redirect=${encodeURIComponent(window.ProData.initHref || window.location.href)}`;
        console.log("redirect url: ", `[${redirectUrl}]`);

        if (S25Util.contains(redirectUrl, "shibboleth")) {
            S25Util.setCookie("S25FirstSSOLogin", true);
        }

        window.location = redirectUrl as string & Location;
    }

    async login() {
        const instanceCookie = `S25Masquerade_${window.ProData.instanceId}`;

        if (S25Util.getCookie(instanceCookie)) {
            S25Util.deleteCookie(instanceCookie);
        }

        const [isLoggedIn, err] = await S25Util.Maybe(LoginService.getLoggedIn());
        if (isLoggedIn) AuthService.authenticate();

        if (this.loginUrl) {
            /*
              ANG-6026 When the user has previously tried to auth, but was denied we need to do a GET to
              login.shibboleth without the ?redirect... so that we get the 'denied' response from 25saas.
            */
            if (S25Util.getCookie("S25FirstSSOLogin")) {
                //Make sure we don't get stuck in this mode, every other sign in click we do a full redirect school's idp to make sure the login state is still oka
                S25Util.deleteCookie("S25FirstSSOLogin");
                const ssoDenied = await LoginService.isLoginDeniedBySSO();
                if (!!ssoDenied) {
                    this.modalPromises.push(
                        await S25Util.all(this.modalPromises).then(() => {
                            return ModalService.modal("alert", {
                                message: `You do not have access to 25Live. If you believe this is an error, please contact your ${
                                    this.adminMailLink
                                        ? `<a href="${this.adminMailLink}">administrator</a>`
                                        : "administrator"
                                }.`,
                            });
                        }),
                    );
                } else {
                    this.redirectToSSO();
                }
            } else {
                this.redirectToSSO();
            }
        } else {
            this.modalPromises.push(
                S25Util.all(this.modalPromises).then(function () {
                    return window.angBridge.$injector.get("s25ModalService").modal("login");
                }),
            );
        }
    }

    /**
     * Wraps the logo element in a link
     */
    wrapLogoInLink() {
        if (this.logoLink && this.logoElement?.nativeElement) {
            const logo = this.logoElement.nativeElement;

            // Create an anchor tag
            const anchor = this.renderer.createElement("a");
            this.renderer.setAttribute(anchor, "href", this.logoLink);
            this.renderer.setAttribute(anchor, "target", "_blank");
            this.renderer.setAttribute(anchor, "rel", "noopener noreferrer");
            this.renderer.setAttribute(anchor, "class", "ngLogoLink");
            this.renderer.setAttribute(anchor, "aria-label", "Link to Home Page");

            // Move the logo element inside the anchor tag
            const parent = logo.parentNode;
            this.renderer.appendChild(anchor, logo);
            this.renderer.appendChild(parent, anchor);
            this.cd.detectChanges();
        }
    }

    async getPreferences() {
        let prefs = await MoreMenuUtil.getPreferences();
        this.logoLink = prefs?.config_logo_link?.value;
        this.adminMailLink = S25Util.eMailLink(prefs?.config_feedback_email?.value);

        S25Util.setDarkMode(prefs?.pro_dark_mode?.value === "T");
        this.wrapLogoInLink();

        const taskString = prefs?.TaskHeaderCount?.value || "Outstanding";
        const taskQueryId = taskString.match(/\d+/);
        this.taskDefault = taskQueryId ? taskQueryId[0] : taskString;
        this.cd.detectChanges();
    }

    /**
     * These are the actions that should happen before the app is initialized
     * It gets login status and determines if login is required
     */
    async getBlockingData() {
        const [auth, prefs] = await S25Util.all([
            LoginService.getLoginCached(),
            PreferenceService.getPreferences(["config_login_required"]),
        ]);

        this.isLoggedIn = auth.isLoggedIn;
        this.loginUrl = auth.loginUrl;
        if (this.isLoggedIn) {
            this.getStyledName();
        } else {
            if (prefs?.config_login_required?.value === "true") {
                //do NOT init app if login required
                await this.login();
            } else {
                this.styledName.set("Guest");
                //else init app as non-logged-in
            }
        }
    }

    ngOnDestroy() {
        this.unreadChatsSubscription?.unsubscribe();
    }
}
